<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden min-h-screen flex items-center pt-16" data-animate="hero" :class="{ 'is-visible': visible.hero }">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300"></div>

      <div class="hero-particles absolute inset-0 overflow-hidden pointer-events-none">
        <div v-for="i in 8" :key="i" class="particle" :style="particleStyle(i)" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-5xl mx-auto text-center">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6 anim-el d1">
            {{ $t('chatbotTicino.hero.tagline') }}
          </p>

          <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6 anim-el d2">
            <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.hero.title') }}</span>
          </h1>

          <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed mb-10 transition-colors duration-300 anim-el d3">
            {{ $t('chatbotTicino.hero.description') }}
            <router-link to="/chatbot" class="text-blue-500 hover:text-blue-600 underline font-semibold transition-colors duration-300" rel="internal">Scopri i nostri chatbot AI</router-link> per soluzioni personalizzate in tutto il Ticino. Il <strong>chatbot Ticino</strong> è la soluzione ideale per le aziende locali che cercano automazione intelligente.
          </p>

          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 anim-el d4">
            <router-link
              to="/contact"
              class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10"
              rel="internal"
              aria-label="Contattaci per un chatbot Ticino personalizzato"
            >
              <MessageSquare class="mr-3 h-6 w-6 transition-transform duration-300 group-hover:rotate-6" aria-hidden="true" />
              {{ $t('chatbotTicino.hero.cta') }}
            </router-link>

            <router-link
              to="/chatbot"
              class="inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-colors duration-300"
              rel="internal"
              aria-label="Scopri di più sui chatbot AI e chatbot Ticino"
            >
              <Bot class="mr-2 h-5 w-5" aria-hidden="true" />
              {{ $t('chatbotTicino.hero.learnMore') }}
            </router-link>
          </div>

          <div class="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 anim-el d5">
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
              24/7 Disponibile
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
              Multilingue (IT/DE/FR)
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
              Made in Ticino
            </div>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <!-- Breadcrumbs for SEO -->
    <nav class="container mx-auto px-4 py-4 transition-colors duration-300" aria-label="Breadcrumb">
      <ol class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400" itemscope itemtype="https://schema.org/BreadcrumbList">
        <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <router-link to="/" class="hover:text-blue-500 transition-colors duration-300" itemprop="item">
            <span itemprop="name">Home</span>
          </router-link>
          <meta itemprop="position" content="1" />
        </li>
        <li class="text-gray-400 dark:text-gray-600" aria-hidden="true">/</li>
        <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <router-link to="/chatbot" class="hover:text-blue-500 transition-colors duration-300" itemprop="item">
            <span itemprop="name">Chatbot</span>
          </router-link>
          <meta itemprop="position" content="2" />
        </li>
        <li class="text-gray-400 dark:text-gray-600" aria-hidden="true">/</li>
        <li class="text-gray-900 dark:text-white font-semibold transition-colors duration-300" aria-current="page" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <span itemprop="name">Chatbot Ticino</span>
          <meta itemprop="position" content="3" />
        </li>
      </ol>
    </nav>

    <!-- Ticino-Specific Benefits Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="ticinobenefits" :class="{ 'is-visible': visible.ticinobenefits }" aria-labelledby="ticino-benefits-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Ticino</p>
          <h2 id="ticino-benefits-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.ticinoSpecific.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.ticinoSpecific.subtitle') }} Un <strong>chatbot Ticino</strong> personalizzato può trasformare il modo in cui la tua azienda interagisce con i clienti.
          </p>
        </header>

        <div class="max-w-5xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d3">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <Globe class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.ticinoSpecific.local.title') }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.ticinoSpecific.local.description') }} Il nostro <strong>chatbot Ticino</strong> è progettato specificamente per il mercato locale.</p>
            </article>
            <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d4">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <Users class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.ticinoSpecific.community.title') }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.ticinoSpecific.community.description') }}</p>
            </article>
          </div>

          <div class="mt-10 text-center anim-el d5">
            <p class="text-gray-500 dark:text-gray-400 mb-4 font-semibold transition-colors duration-300">{{ $t('chatbotTicino.ticinoSpecific.learnMore') }}</p>
            <router-link
              to="/chatbot"
              class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10"
              aria-label="Scopri di più sui chatbot AI e chatbot Ticino"
              rel="internal"
            >
              <Bot class="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-6" aria-hidden="true" />
              {{ $t('chatbotTicino.ticinoSpecific.exploreGeneral') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Technology & Local Expertise Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="technology" :class="{ 'is-visible': visible.technology }" aria-labelledby="technology-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Tecnologia</p>
          <h2 id="technology-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.technology.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.technology.subtitle') }} La tecnologia avanzata del nostro <strong>chatbot Ticino</strong> garantisce prestazioni ottimali.
          </p>
        </header>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
          <article class="anim-el d3">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.title') }}</h3>
            <ul class="space-y-4" role="list">
              <li class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-lg bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300" aria-hidden="true">
                  <Check class="h-3.5 w-3.5 text-blue-500" />
                </div>
                <div>
                  <h4 class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.n8n.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.n8n.description') }}</p>
                </div>
              </li>
              <li class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-lg bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300" aria-hidden="true">
                  <Check class="h-3.5 w-3.5 text-blue-500" />
                </div>
                <div>
                  <h4 class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.botpress.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.botpress.description') }}</p>
                </div>
              </li>
              <li class="flex items-start space-x-3">
                <div class="w-6 h-6 rounded-lg bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300" aria-hidden="true">
                  <Check class="h-3.5 w-3.5 text-blue-500" />
                </div>
                <div>
                  <h4 class="text-gray-900 dark:text-white font-medium transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.python.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.stack.python.description') }}</p>
                </div>
              </li>
            </ul>
          </article>

          <article class="anim-el d4">
            <h3 class="text-2xl font-bold mb-6 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.technology.local.title') }}</h3>
            <div class="space-y-4" role="list">
              <article class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.multilingual.title') }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.multilingual.description') }}</p>
              </article>
              <article class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.compliance.title') }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.compliance.description') }}</p>
              </article>
              <article class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300">
                <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.support.title') }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.technology.local.support.description') }}</p>
              </article>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- What is a Chatbot Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="whatischatbot" :class="{ 'is-visible': visible.whatischatbot }" aria-labelledby="what-is-chatbot-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Cos'è un chatbot</p>
          <h2 id="what-is-chatbot-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.whatIsChatbot.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.whatIsChatbot.subtitle') }} Un <strong>chatbot Ticino</strong> è la soluzione perfetta per automatizzare le comunicazioni aziendali.
          </p>
        </header>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-medium">{{ $t('chatbotTicino.whatIsChatbot.problems.title') }}</span>
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
            </div>
            <ul class="space-y-4" role="list">
              <li class="flex items-start gap-3 anim-el d3">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.waiting.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.waiting.description') }}</p>
                </div>
              </li>
              <li class="flex items-start gap-3 anim-el d4">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.language.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.language.description') }}</p>
                </div>
              </li>
              <li class="flex items-start gap-3 anim-el d5">
                <span class="text-red-400 text-xl font-bold flex-shrink-0">×</span>
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.costs.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.problems.costs.description') }}</p>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-blue-500 font-medium">{{ $t('chatbotTicino.whatIsChatbot.solutions.title') }}</span>
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
            </div>
            <ul class="space-y-4" role="list">
              <li class="flex items-start gap-3 anim-el d3">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.immediate.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.immediate.description') }}</p>
                </div>
              </li>
              <li class="flex items-start gap-3 anim-el d4">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.multilingual.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.multilingual.description') }}</p>
                </div>
              </li>
              <li class="flex items-start gap-3 anim-el d5">
                <Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.costs.title') }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbotTicino.whatIsChatbot.solutions.costs.description') }}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits for Ticino SMEs Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="benefits" :class="{ 'is-visible': visible.benefits }" aria-labelledby="benefits-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Vantaggi</p>
          <h2 id="benefits-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.benefits.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.benefits.subtitle') }} Scopri perché un <strong>chatbot Ticino</strong> è essenziale per la tua azienda.
          </p>
        </header>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" role="list">
          <article v-for="(benefit, idx) in benefitsList" :key="idx" class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el" :class="'d' + (idx + 3)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
              <component :is="benefit.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
            </div>
            <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ benefit.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ benefit.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Local Use Cases Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="usecases" :class="{ 'is-visible': visible.usecases }" aria-labelledby="use-cases-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Casi d'uso</p>
          <h2 id="use-cases-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.useCases.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.useCases.subtitle') }} Ecco come un <strong>chatbot Ticino</strong> può aiutare diversi settori.
          </p>
        </header>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" role="list">
          <!-- Tourism -->
          <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d3">
            <div class="flex items-center mb-5">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <Building class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.useCases.tourism.title') }}</h3>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.useCases.tourism.description') }} Un <strong>chatbot Ticino</strong> per il turismo migliora l'esperienza dei visitatori.</p>
            <ul class="space-y-2 text-sm text-gray-500 dark:text-gray-400" role="list">
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.tourism.features.bookings') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.tourism.features.info') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.tourism.features.requests') }}</li>
            </ul>
            <blockquote class="mt-4 rounded-xl p-4 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <p class="text-sm text-gray-500 dark:text-gray-400 font-medium italic">"{{ $t('chatbotTicino.useCases.tourism.testimonial.text') }}"</p>
              <footer class="text-xs text-gray-400 dark:text-gray-500 mt-1">- {{ $t('chatbotTicino.useCases.tourism.testimonial.author') }}</footer>
            </blockquote>
          </article>

          <!-- Restaurants -->
          <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d4">
            <div class="flex items-center mb-5">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <UtensilsCrossed class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.useCases.restaurants.title') }}</h3>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.useCases.restaurants.description') }} Il <strong>chatbot Ticino</strong> per ristoranti semplifica le prenotazioni.</p>
            <ul class="space-y-2 text-sm text-gray-500 dark:text-gray-400" role="list">
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.restaurants.features.bookings') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.restaurants.features.management') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.restaurants.features.reminders') }}</li>
            </ul>
            <blockquote class="mt-4 rounded-xl p-4 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <p class="text-sm text-gray-500 dark:text-gray-400 font-medium italic">"{{ $t('chatbotTicino.useCases.restaurants.testimonial.text') }}"</p>
              <footer class="text-xs text-gray-400 dark:text-gray-500 mt-1">- {{ $t('chatbotTicino.useCases.restaurants.testimonial.author') }}</footer>
            </blockquote>
          </article>

          <!-- Medical -->
          <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d5">
            <div class="flex items-center mb-5">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <Heart class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.useCases.medical.title') }}</h3>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.useCases.medical.description') }} Un <strong>chatbot Ticino</strong> per strutture mediche ottimizza la gestione degli appuntamenti.</p>
            <ul class="space-y-2 text-sm text-gray-500 dark:text-gray-400" role="list">
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.medical.features.appointments') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.medical.features.reminders') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.medical.features.emergencies') }}</li>
            </ul>
            <blockquote class="mt-4 rounded-xl p-4 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <p class="text-sm text-gray-500 dark:text-gray-400 font-medium italic">"{{ $t('chatbotTicino.useCases.medical.testimonial.text') }}"</p>
              <footer class="text-xs text-gray-400 dark:text-gray-500 mt-1">- {{ $t('chatbotTicino.useCases.medical.testimonial.author') }}</footer>
            </blockquote>
          </article>

          <!-- E-commerce -->
          <article class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d6">
            <div class="flex items-center mb-5">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                <ShoppingCart class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.useCases.ecommerce.title') }}</h3>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.useCases.ecommerce.description') }} Il <strong>chatbot Ticino</strong> per e-commerce aumenta le conversioni.</p>
            <ul class="space-y-2 text-sm text-gray-500 dark:text-gray-400" role="list">
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.ecommerce.features.tracking') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.ecommerce.features.returns') }}</li>
              <li class="flex items-center"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />{{ $t('chatbotTicino.useCases.ecommerce.features.recommendations') }}</li>
            </ul>
            <blockquote class="mt-4 rounded-xl p-4 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <p class="text-sm text-gray-500 dark:text-gray-400 font-medium italic">"{{ $t('chatbotTicino.useCases.ecommerce.testimonial.text') }}"</p>
              <footer class="text-xs text-gray-400 dark:text-gray-500 mt-1">- {{ $t('chatbotTicino.useCases.ecommerce.testimonial.author') }}</footer>
            </blockquote>
          </article>
        </div>
      </div>
    </section>

    <!-- 24/7 Support Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="support" :class="{ 'is-visible': visible.support }" aria-labelledby="support-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Supporto 24/7</p>
          <h2 id="support-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.support24_7.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.support24_7.subtitle') }} Il nostro <strong>chatbot Ticino</strong> offre supporto continuo 24 ore su 24.
          </p>
        </header>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
          <article class="anim-el d3">
            <ul class="space-y-6" role="list">
              <li class="group flex items-start space-x-4">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                  <Clock class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
                </div>
                <div>
                  <h3 class="text-lg font-bold mb-1 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.availability.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.availability.description') }}</p>
                </div>
              </li>

              <li class="group flex items-start space-x-4">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                  <Globe class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
                </div>
                <div>
                  <h3 class="text-lg font-bold mb-1 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.multilingual.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.multilingual.description') }}</p>
                </div>
              </li>

              <li class="group flex items-start space-x-4">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
                  <Users class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
                </div>
                <div>
                  <h3 class="text-lg font-bold mb-1 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.scalability.title') }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('chatbotTicino.support24_7.features.scalability.description') }}</p>
                </div>
              </li>
            </ul>
          </article>

          <article class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el d4">
            <h3 class="text-lg font-bold mb-5 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbotTicino.support24_7.conversation.title') }}</h3>
            <div class="space-y-4" role="dialog" aria-label="Esempio di conversazione chatbot">
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center flex-shrink-0 transition-colors duration-300" aria-hidden="true">
                  <Users class="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div class="rounded-xl p-3 max-w-xs bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('chatbotTicino.support24_7.conversation.customer1') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3 justify-end">
                <div class="rounded-xl p-3 max-w-xs bg-gray-900 dark:bg-white/[0.08] border border-gray-800 dark:border-white/[0.1] transition-colors duration-300">
                  <p class="text-sm text-white dark:text-gray-300">{{ $t('chatbotTicino.support24_7.conversation.bot1') }}</p>
                </div>
                <div class="w-8 h-8 rounded-lg bg-gray-900 dark:bg-white/[0.1] border border-gray-700 dark:border-white/[0.12] flex items-center justify-center flex-shrink-0 transition-colors duration-300" aria-hidden="true">
                  <Bot class="h-4 w-4 text-white dark:text-gray-400" />
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center flex-shrink-0 transition-colors duration-300" aria-hidden="true">
                  <Users class="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div class="rounded-xl p-3 max-w-xs bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('chatbotTicino.support24_7.conversation.customer2') }}</p>
                </div>
              </div>
              <div class="flex items-start space-x-3 justify-end">
                <div class="rounded-xl p-3 max-w-xs bg-gray-900 dark:bg-white/[0.08] border border-gray-800 dark:border-white/[0.1] transition-colors duration-300">
                  <p class="text-sm text-white dark:text-gray-300">{{ $t('chatbotTicino.support24_7.conversation.bot2') }}</p>
                </div>
                <div class="w-8 h-8 rounded-lg bg-gray-900 dark:bg-white/[0.1] border border-gray-700 dark:border-white/[0.12] flex items-center justify-center flex-shrink-0 transition-colors duration-300" aria-hidden="true">
                  <Bot class="h-4 w-4 text-white dark:text-gray-400" />
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- AI Technology Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="aitech" :class="{ 'is-visible': visible.aitech }" aria-labelledby="ai-technology-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">AI</p>
          <h2 id="ai-technology-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.technology.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.technology.subtitle') }} La tecnologia AI del <strong>chatbot Ticino</strong> è all'avanguardia.
          </p>
        </header>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" role="list">
          <article v-for="(tech, idx) in techList" :key="idx" class="group rounded-2xl p-6 text-center bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-300 anim-el" :class="'d' + (idx + 3)">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 group-hover:scale-110" aria-hidden="true">
              <component :is="tech.icon" class="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" aria-hidden="true" />
            </div>
            <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ tech.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ tech.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Summary Section with Internal Links -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="summary" :class="{ 'is-visible': visible.summary }" aria-labelledby="summary-heading">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto rounded-2xl p-8 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300 anim-el d1">
          <h2 id="summary-heading" class="text-2xl font-bold mb-6 text-gray-900 dark:text-white text-center transition-colors duration-300">
            Soluzioni Chatbot AI per Aziende in Ticino
          </h2>
          <div class="max-w-none text-gray-500 dark:text-gray-400 transition-colors duration-300 space-y-4">
            <p>
              I <strong>chatbot AI per il Ticino</strong> rappresentano la soluzione ideale per migliorare il servizio clienti e automatizzare le comunicazioni. Un <strong>chatbot Ticino</strong> con supporto multilingue (italiano, tedesco, francese) è perfetto per il mercato svizzero. Il <strong>chatbot Ticino</strong> offre vantaggi unici per le aziende locali.
            </p>
            <p>
              Il mercato ticinese presenta sfide uniche: clienti da tutta la Svizzera, necessità di supporto 24/7 durante la stagione turistica, e costi elevati del personale. Un <strong>chatbot Ticino</strong> risolve questi problemi, permettendo alle PMI di competere offrendo servizio clienti enterprise a una frazione del costo. Il <strong>chatbot Ticino</strong> è la scelta intelligente per l'automazione.
            </p>
            <p>
              Scopri i nostri <router-link to="/chatbot" class="text-blue-500 hover:text-blue-600 underline font-semibold transition-colors duration-300" rel="internal">chatbot AI personalizzati</router-link> o i nostri <router-link to="/website-ticino" class="text-blue-500 hover:text-blue-600 underline font-semibold transition-colors duration-300" rel="internal">siti web moderni per il Ticino</router-link> con <strong>chatbot Ticino</strong> integrato.
            </p>
            <p>
              Soluzioni <strong>chatbot Ticino</strong> sviluppate localmente, con conformità svizzera e supporto in italiano. Il <strong>chatbot Ticino</strong> è progettato per le esigenze specifiche del mercato locale. <router-link to="/contact" class="text-blue-500 hover:text-blue-600 underline font-semibold transition-colors duration-300" rel="internal">Consulenza gratuita</router-link> disponibile per il tuo <strong>chatbot Ticino</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Process Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="process" :class="{ 'is-visible': visible.process }" aria-labelledby="process-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-20">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">Processo</p>
          <h2 id="process-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.process.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.process.subtitle') }} Il processo di sviluppo del tuo <strong>chatbot Ticino</strong> è semplice e veloce.
          </p>
        </header>

        <div class="max-w-3xl mx-auto space-y-0">
          <div v-for="(step, index) in processSteps" :key="index" class="relative flex gap-6 md:gap-10 anim-el" :class="'d' + (index + 1)">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.1] flex items-center justify-center text-sm font-bold text-gray-900 dark:text-white flex-shrink-0 transition-colors duration-300">
                {{ index + 1 }}
              </div>
              <div v-if="index < processSteps.length - 1" class="w-px flex-1 bg-gray-200 dark:bg-white/[0.06] my-2 transition-colors duration-300"></div>
            </div>
            <div class="pb-14 flex-1" :class="{ 'pb-0': index === processSteps.length - 1 }">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ step.title }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="faq" :class="{ 'is-visible': visible.faq }" aria-labelledby="faq-heading">
      <div class="container mx-auto px-4">
        <header class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4 anim-el d1">FAQ</p>
          <h2 id="faq-heading" class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300 anim-el d1">
            {{ $t('chatbotTicino.faq.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 anim-el d2">
            {{ $t('chatbotTicino.faq.subtitle') }} Domande frequenti sul <strong>chatbot Ticino</strong> e le sue funzionalità.
          </p>
        </header>

        <div class="max-w-3xl mx-auto anim-el d3" itemscope itemtype="https://schema.org/FAQPage">
          <div class="divide-y divide-gray-200 dark:divide-white/[0.06]">
            <div v-for="(faq, index) in faqQuestions" :key="index" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
              <button
                @click="toggleFaq(index)"
                class="group w-full flex justify-between items-center py-5 text-left"
                :aria-expanded="activeFaq === index"
                :aria-controls="`faq-answer-${index}`"
              >
                <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300 group-hover:text-blue-500" itemprop="name">{{ faq.question }}</h3>
                <ChevronDown class="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-all duration-300" :class="{ 'rotate-180': activeFaq === index }" />
              </button>
              <div
                v-show="activeFaq === index"
                :id="`faq-answer-${index}`"
                class="pb-5 -mt-1"
                itemscope
                itemprop="acceptedAnswer"
                itemtype="https://schema.org/Answer"
              >
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300" itemprop="text">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="relative py-28 md:py-40 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>

      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-300 mb-6">
            Chatbot Ticino Personalizzato
          </div>

          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4">
            {{ $t('chatbotTicino.cta.title') }}
          </h2>
          <p class="text-xl md:text-2xl mb-8 text-white/90 font-semibold max-w-3xl mx-auto">
            {{ $t('chatbotTicino.cta.description') }} Inizia oggi con il tuo <strong>chatbot Ticino</strong> personalizzato.
          </p>

          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <router-link
              to="/contact"
              class="group inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10"
              rel="internal"
              aria-label="Richiedi un preventivo per chatbot Ticino - Soluzioni chatbot Ticino personalizzate"
            >
              <MessageSquare class="mr-3 h-6 w-6 transition-transform duration-300 group-hover:rotate-6" aria-hidden="true" />
              {{ $t('chatbotTicino.cta.button') }}
            </router-link>
            <router-link
              to="/chatbot"
              class="inline-flex items-center h-14 px-8 border border-white/20 text-white rounded-xl font-semibold text-lg"
              rel="internal"
            >
              <Bot class="mr-2 h-5 w-5" aria-hidden="true" />
              {{ $t('chatbotTicino.hero.learnMore') }}
            </router-link>
          </div>

          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90">
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
              24/7 Disponibile
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
              Multilingue (IT/DE/FR)
            </div>
            <div class="flex items-center">
              <Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
              Made in Ticino
            </div>
          </div>
        </div>
      </div>
    </section>

    <FloatingCta />
  </div>
</template>

<script setup>
import { MessageSquare, Globe, Clock, Users, Bot, Zap, Shield, Brain, Check, ChevronDown, Building, Heart, ShoppingCart, UtensilsCrossed } from 'lucide-vue-next'
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import FloatingCta from '../components/FloatingCta.vue'

const { tm, t } = useI18n()
const activeFaq = ref(null)
const visible = reactive({})
let observer = null

const faqQuestions = computed(() => {
  const questions = tm('chatbotTicino.faq.questions')
  return Array.isArray(questions) ? questions : []
})

function particleStyle(i) {
  const left = ((i * 17 + 5) % 90) + 5
  const dur = 14 + (i % 4) * 2
  const delay = (i * 1.3) % 6
  const size = 2 + (i % 3)
  return {
    position: 'absolute',
    bottom: '-10px',
    left: left + '%',
    width: size + 'px',
    height: size + 'px',
    borderRadius: '50%',
    background: 'rgba(59, 130, 246, 0.5)',
    animation: `particleRise ${dur}s linear ${delay}s infinite`
  }
}

const toggleFaq = (index) => {
  activeFaq.value = activeFaq.value === index ? null : index
}

const benefitsList = [
  { icon: Globe, title: t('chatbotTicino.benefits.multilingual.title'), description: t('chatbotTicino.benefits.multilingual.description') },
  { icon: Clock, title: t('chatbotTicino.benefits.availability.title'), description: t('chatbotTicino.benefits.availability.description') },
  { icon: Users, title: t('chatbotTicino.benefits.customerService.title'), description: t('chatbotTicino.benefits.customerService.description') },
  { icon: Zap, title: t('chatbotTicino.benefits.efficiency.title'), description: t('chatbotTicino.benefits.efficiency.description') },
  { icon: Shield, title: t('chatbotTicino.benefits.security.title'), description: t('chatbotTicino.benefits.security.description') },
  { icon: Brain, title: t('chatbotTicino.benefits.intelligence.title'), description: t('chatbotTicino.benefits.intelligence.description') }
]

const techList = [
  { icon: Bot, title: t('chatbotTicino.technologies.nlp.title'), description: t('chatbotTicino.technologies.nlp.description') },
  { icon: Brain, title: t('chatbotTicino.technologies.ml.title'), description: t('chatbotTicino.technologies.ml.description') },
  { icon: Globe, title: t('chatbotTicino.technologies.integration.title'), description: t('chatbotTicino.technologies.integration.description') },
  { icon: Shield, title: t('chatbotTicino.technologies.security.title'), description: t('chatbotTicino.technologies.security.description') }
]

const processSteps = [
  { title: t('chatbotTicino.process.analysis.title'), description: t('chatbotTicino.process.analysis.description') },
  { title: t('chatbotTicino.process.design.title'), description: t('chatbotTicino.process.design.description') },
  { title: t('chatbotTicino.process.development.title'), description: t('chatbotTicino.process.development.description') },
  { title: t('chatbotTicino.process.deployment.title'), description: t('chatbotTicino.process.deployment.description') }
]

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        visible[entry.target.dataset.animate] = true
      }
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })

  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el))

  // JSON-LD Structured Data for SEO
  const faqSchema = faqQuestions.value.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t('chatbotTicino.hero.title'),
    description: t('chatbotTicino.hero.description'),
    provider: {
      '@type': 'LocalBusiness',
      name: 'Ciobai',
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'Ticino',
        addressCountry: 'CH'
      }
    },
    areaServed: {
      '@type': 'State',
      name: 'Ticino'
    },
    serviceType: 'AI Chatbot Development',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href
    }
  }

  if (faqSchema.length > 0) {
    structuredData.mainEntity = {
      '@type': 'FAQPage',
      mainEntity: faqSchema
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(structuredData)
  script.id = 'chatbot-ticino-structured-data'
  document.head.appendChild(script)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  const script = document.getElementById('chatbot-ticino-structured-data')
  if (script) {
    script.remove()
  }
})

defineOptions({
  name: 'ChatbotTicinoPage'
})
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el {
  opacity: 1;
  transform: translateY(0);
}
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
.d6 { transition-delay: 0.55s; }
.d7 { transition-delay: 0.65s; }
.d8 { transition-delay: 0.75s; }
.d9 { transition-delay: 0.85s; }

@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.7; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.3; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}
</style>
